/* ------------------------------------------------------------------------
 * What a coverage is called, versus what it is.
 *
 * Section 6 told Aspen View Academy it had NO sexual abuse and molestation
 * cover — "CRITICAL GAP · Immediately propose and bind" — for a charter
 * school whose package policy schedules:
 *
 *     GCG 74 10   07 14   Sexual Misconduct Liability Coverage (Occurrence)
 *         Sexual Misconduct Liability Each Claim Limit      $1,000,000
 *         Sexual Misconduct Liability Aggregate Limit       $3,000,000
 *
 * It also told them to "request and review current Equipment Breakdown"
 * cover they already buy under PCP 73 57.
 *
 * The matcher compared names by substring in both directions. "Sexual
 * Misconduct Liability" and "Sexual Abuse / Molestation" share no substring,
 * so a coverage that is on the policy was reported as absent — the most
 * damaging output this product can produce, because it is confident, specific
 * and wrong, and the recommended action is to buy something twice.
 *
 * Substring matching also fails the other way. It is what let a short name
 * swallow a longer one, and it cannot tell that "Boiler and Machinery" and
 * "Equipment Breakdown" are the same thing while "Employers Liability" and
 * "Employment Practices Liability" are not, despite sharing more letters.
 *
 * So coverages are compared by CONCEPT. Each class below lists the ways the
 * market writes one coverage: the ISO name, the carrier trade names, the
 * abbreviation, and the older name the form is still sold under. Matching is
 * on whole terms, so "Employers Liability" cannot match the Employment
 * Practices class.
 * ------------------------------------------------------------------------ */

(function () {
  'use strict';
  const root = (typeof window !== 'undefined') ? window : globalThis;

  // canonical name -> every wording that means it.
  // Order within a class does not matter; order BETWEEN classes does, because
  // the first class whose term matches wins, so put the specific ones first.
  const CLASSES = [
    ['Sexual Abuse / Molestation', [
      'sexual abuse', 'sexual misconduct', 'sexual molestation', 'molestation',
      'abuse and molestation', 'abuse or molestation', 'abuse & molestation',
      'sexual abuse and molestation', 'sam coverage', 'sam liability',
      'improper sexual conduct', 'sexual abuse liability', 'misconduct liability',
      'physical abuse', 'child abuse' ]],

    ['Equipment Breakdown', [
      'equipment breakdown', 'boiler and machinery', 'boiler & machinery',
      'boiler machinery', 'mechanical breakdown', 'machinery breakdown',
      'systems breakdown', 'equipment failure' ]],

    ['Educators Legal Liability', [
      'educators legal liability', 'educator legal liability', 'ell coverage',
      // GuideOne writes the coverage part as "Educators Management Liability"
      // and designates the sub-coverages EL, DO and EP beneath it. Without
      // this the declarations line that states the limit was unreadable.
      'educators management liability',
      'school leaders errors and omissions', 'school board legal liability',
      'school leaders liability', 'educators liability' ]],

    ['Employment Practices Liability', [
      'employment practices liability', 'employment related practices',
      'employment-related practices', 'epli', 'epl coverage', 'wrongful termination liability' ]],

    ['Employee Benefits Liability', [
      'employee benefits liability', 'employee benefit liability', 'ebl coverage',
      'employee benefits administration liability' ]],

    ['Directors & Officers', [
      'directors and officers', 'directors & officers', 'd&o liability', 'd&o',
      'management liability', 'organizational liability' ]],

    ['Crime / Fidelity', [
      'crime coverage', 'commercial crime', 'fidelity bond', 'fidelity coverage',
      'employee dishonesty', 'employee theft', 'crime and fidelity',
      'financial institution bond' ]],

    ['Student Accident', [
      'student accident', 'blanket accident', 'blanket student accident',
      'participant accident', 'accident medical expense', 'student insurance',
      'athletic accident' ]],

    ['Active Shooter / Violent Event', [
      'active shooter', 'active assailant', 'deadly weapon', 'violent event',
      'workplace violence', 'malicious attack', 'violent malicious act' ]],

    ['Cyber Liability', [
      'cyber liability', 'cyber suite', 'cyber insurance', 'privacy and network security',
      'network security and privacy', 'information security and privacy',
      'data breach', 'breach response', 'privacy liability', 'technology errors' ]],

    ['Workers Compensation', [
      'workers compensation', "workers' compensation", 'workmens compensation',
      'workers comp', 'work comp', 'wc coverage',
      // Deliberately NOT the bare abbreviation "EL": Educators Legal
      // Liability uses it too, and on a school policy both are present.
      'employers liability', "employer's liability" ]],

    ['Excess / Umbrella Liability', [
      'commercial liability umbrella', 'umbrella liability', 'commercial umbrella',
      'excess liability', 'excess follow form', 'umbrella coverage' ]],

    ['Commercial Auto', [
      'commercial auto', 'business auto', 'automobile liability', 'auto liability',
      'hired and non-owned', 'hired & non-owned', 'hired auto', 'non-owned auto',
      'garage liability' ]],

    ['Commercial Property', [
      'commercial property', 'building and personal property', 'property coverage',
      'building coverage', 'business personal property', 'blanket property' ]],

    ['Business Income', [
      'business income', 'business interruption', 'extra expense',
      'loss of income', 'time element' ]],

    ['Professional Liability / E&O', [
      'professional liability', 'errors and omissions', 'errors & omissions',
      'e&o coverage', 'miscellaneous professional' ]],

    ['Pollution / Environmental Liability', [
      'pollution liability', 'pollution legal liability', 'environmental impairment',
      'contractors pollution', 'environmental liability' ]],

    ['Flood', ['flood insurance', 'flood coverage', 'nfip']],

    ['General Liability', [
      'commercial general liability', 'general liability', 'cgl coverage',
      'premises liability', 'bodily injury and property damage liability' ]]
  ];

  // A term matches on whole words, so "employers liability" cannot be found
  // inside "employment practices liability" and vice versa. Built once.
  const COMPILED = CLASSES.map(([name, terms]) => ({
    name,
    terms,
    res: terms.map(t => new RegExp('(^|[^a-z0-9])' +
      t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '[\\s\\-/&]+') +
      '([^a-z0-9]|$)', 'i'))
  }));

  /** The canonical coverage a name refers to, or null. */
  /* NAMES THE ENGINE USES FOR ITSELF, WHICH ARE NOT PHRASES IN A POLICY.
   *
   * detectCoveragesFromPdfText emits "Property"; the limits map, the
   * benchmarking table and this vocabulary all say "Commercial Property".
   * canonical('Property') was null, so sameTerm('Property','Commercial
   * Property') was FALSE and any code comparing the two silently disagreed.
   *
   * That seam put the wrong policy under a finding: the parse ledger scores
   * candidate documents by asking whether the detector agrees the coverage is
   * in them, the answer was always no, every document tied, and the ledger
   * fell back to upload order — so Tulsa Honor Academy's Commercial Property
   * row cited the AUTO policy's declarations and endorsements.
   *
   * Kept OUT of the regex table on purpose. These are exact-name aliases for
   * comparing two labels; adding a bare "property" as a matchable term would
   * make coveragesIn fire on "property damage", which is in every liability
   * policy ever written.
   */
  const NAME_ALIASES = new Map([
    ['property', 'Commercial Property'],
    ['crime/fidelity', 'Crime / Fidelity'],
    ['umbrella/excess', 'Excess / Umbrella Liability'],
    ['excess/umbrella', 'Excess / Umbrella Liability'],
    ['epli', 'Employment Practices Liability'],
    ['d&o', 'Directors & Officers'],
    ['educators legal liability (ell)', 'Educators Legal Liability'],
    ['sexual abuse/molestation', 'Sexual Abuse / Molestation'],
    ['professional liability', 'Professional Liability / E&O'],
    ['e&o', 'Professional Liability / E&O'],
    // The limits extractor keys this one 'Crime'. Without the alias it
    // canonicalises to null, sameTerm returns false against 'Crime / Fidelity',
    // and a real coverage on Matrix Head Start's D&O policy was dropped.
    ['crime', 'Crime / Fidelity'],
    // A CANONICAL NAME MUST CANONICALISE TO ITSELF.
    //
    // canonical('Crime/Fidelity') returned 'Crime / Fidelity' and
    // canonical('Crime / Fidelity') returned null, so the function's own
    // output was not a valid input to it and sameTerm(canonical(x), x) could
    // be false. It cost two detours: the Crime coverage part was invisible to
    // its own limits spec, and a real Crime limit was withdrawn from Matrix
    // Head Start's D&O policy.
    //
    // These are the only two of the nineteen classes whose display name is not
    // itself one of the class's match terms — 'Crime / Fidelity' because the
    // terms are written "crime coverage", "fidelity bond" and the like, and
    // 'Flood' because the class carries no bare "flood" term. Registering all
    // nineteen was the first attempt and it broke the property path; these two
    // are what the round trip actually needs.
    ['crime / fidelity', 'Crime / Fidelity'],
    ['flood', 'Flood']
  ]);

  // canonical() round-trips: every class name is a valid input to canonical()
  // and returns itself. Seventeen of the nineteen classes get that from their
  // own match terms; 'Crime / Fidelity' and 'Flood' do not, and are aliased
  // above. A test asserts the property for all of them, because the failure
  // mode is silent — sameTerm(canonical(x), x) returning false made the Crime
  // coverage part invisible to its own limits spec and withdrew a real Crime
  // limit from a policy that carries it.


  // Every alias must name a class that exists, or sameTerm quietly compares a
  // real canonical name against a string nothing else uses — which is the bug
  // this map was added to fix, reintroduced one level down.
  if (typeof console !== 'undefined') {
    const real = new Set(CLASSES.map(c => (Array.isArray(c) ? c[0] : c.name)));
    for (const target of NAME_ALIASES.values()) {
      if (!real.has(target)) console.warn('[coverageTerms] alias target is not a class: ' + target);
    }
  }

  function canonical(name) {
    const s = String(name || '');
    if (!s.trim()) return null;
    const alias = NAME_ALIASES.get(s.toLowerCase().replace(/\s+/g, ' ').trim());
    if (alias) return alias;
    for (const c of COMPILED) {
      for (const re of c.res) if (re.test(s)) return c.name;
    }
    return null;
  }

  /** Do two coverage names refer to the same coverage? Falls back to a
   *  whole-string comparison when neither is in the vocabulary, so an
   *  unlisted coverage still matches itself. */
  function sameTerm(a, b) {
    const ca = canonical(a), cb = canonical(b);
    if (ca && cb) return ca === cb;
    const na = String(a || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const nb = String(b || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (!na || !nb) return false;
    return na === nb;
  }

  /** Every canonical coverage named anywhere in a block of text. Used to read
   *  a policy's forms schedule, which is the policy's own statement of what it
   *  attaches — stronger evidence than a keyword found in a form body. */
  function coveragesIn(text) {
    const s = String(text || '');
    const out = [];
    for (const c of COMPILED) {
      for (const re of c.res) {
        if (re.test(s)) { out.push(c.name); break; }
      }
    }
    return out;
  }

  /** Every position in `text` where the named coverage is mentioned, as
   *  [{ index, term }]. Callers use the index to ask where the mention SITS —
   *  a coverage named only inside an underlying schedule or an exclusion title
   *  is named as something the policy is not covering. */
  const _MENTION_RE = new Map();

  function mentionsOf(text, name) {
    const s = String(text || '');
    const canon = canonical(name);
    const cls = canon ? COMPILED.find(c => c.name === canon) : null;
    const out = [];
    // An unlisted coverage still deserves an answer: look for its own name.
    const terms = cls ? cls.terms : [String(name || '').trim()].filter(Boolean);
    for (const t of terms) {
      // Compiled once. mentionsOf is asked for every coverage on every page of
      // every document — nineteen coverages across 277 recovered pages is five
      // thousand calls, and each was building ten regexes from scratch.
      let re = _MENTION_RE.get(t);
      if (!re) {
        re = new RegExp('(^|[^a-z0-9])(' +
          t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '[\\s\\-/&]+') +
          ')([^a-z0-9]|$)', 'gi');
        _MENTION_RE.set(t, re);
      }
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(s)) !== null) {
        out.push({ index: m.index + m[1].length, term: m[2] });
        if (re.lastIndex <= m.index) re.lastIndex = m.index + 1;
      }
    }
    return out.sort((a, b) => a.index - b.index);
  }

  root.spCoverageTerms = { canonical, sameTerm, coveragesIn, mentionsOf, CLASSES, NAME_ALIASES };
  if (typeof module !== 'undefined' && module.exports) module.exports = root.spCoverageTerms;
})();
